import React, {useCallback, useEffect} from 'react';
import {observer} from 'mobx-react';

import {useAction, useNavigate, useStore, useUrlParams, useUserWidgets} from 'shared/hooks';
import {UiButton, UiDataBoundary, UiFilter, UiList, UiPagination} from 'shared/uikit';
import {OnChangeEventType} from 'shared/types';
import {ApplicationModule} from 'shared/modules';
import {LayoutAside, LayoutBody, LayoutContent, LayoutContentTitle} from 'shared/layout';
import {ROUTES} from 'shared/constants';
import {DepartmentBranchModel} from "shared/models";
import {vacanciesDepartmentBranchesQuery} from 'shared/queries';

import VacancyItem from './components/VacancyItem';

import Store from './Store';
import Service from './Service';

import './styles.scss';

export const VacanciesPage = observer(() => {
    const store = useStore(() => ({
        departmentBranches: [] as DepartmentBranchModel[],
        isDepartmentBranchesLoaded: false,
        isVacancyRegionsLoaded: false,
    }));
    const navigate = useNavigate();
    const urlParams = useUrlParams({
        page: 1,
        vacancyRegionIds: [] as number[],
        departmentBranchId: null as number | null,
    });

    const fetchDepartmentBranches = useCallback(async () => {
        store.set("isDepartmentBranchesLoaded", false);
        const {isSuccess, data} = await vacanciesDepartmentBranchesQuery({
            vacancyRegionIds: urlParams.vacancyRegionIds,
        });
        if (isSuccess && data) {
            store.set("departmentBranches", data.items.map(item => new DepartmentBranchModel(item)));
        }
        store.set("isDepartmentBranchesLoaded", true);
    }, [store, urlParams.vacancyRegionIds]);

    useEffect(() => {
        fetchDepartmentBranches();
    }, [fetchDepartmentBranches]);

    useEffect(() => {
        ApplicationModule.setTitle('Вакансии');
    });

    const handleChangePage = ({value}: OnChangeEventType, isInfinite: boolean) => {
        Store.isInfiniteLoading = isInfinite;
        navigate(ROUTES.VACANCIES(), {...urlParams, page: value});
    };

    const handleChangeVacancyRegionIds = useAction(({value}: OnChangeEventType<number[]>) => {
        Store.pages = 0;
        navigate(ROUTES.VACANCIES(), {
            ...urlParams,
            page: 1,
            vacancyRegionIds: value,
        });
    }, [urlParams]);

    const handleChangeDepartmentBranchId = useAction(({value}: OnChangeEventType<number | null>) => {
        Store.pages = 0;
        navigate(ROUTES.VACANCIES(), {
            ...urlParams,
            page: 1,
            departmentBranchId: value,
        });
    }, [urlParams]);

    const handleResetFilters = () => {
        Store.pages = 0;
        navigate(ROUTES.VACANCIES(), {
            ...urlParams,
            page: 1,
            departmentBranchId: null,
            vacancyRegionIds: [],
        });
    };

    const handleClear = () => {
        navigate(ROUTES.VACANCIES());
    };

    useEffect(() => {
        (async () => {
            store.set("isVacancyRegionsLoaded", false);
            await Service.fetchItems(urlParams);
            store.set("isVacancyRegionsLoaded", true);
        })();
    }, [store, urlParams]);

    useEffect(() => {
        if (!store.isDepartmentBranchesLoaded || urlParams.departmentBranchId === null) {
            return;
        }

        const hasDepartmentBranch = store.departmentBranches.some(item => item.id === urlParams.departmentBranchId);
        if (hasDepartmentBranch) {
            return;
        }

        navigate(ROUTES.VACANCIES(), {
            ...urlParams,
            page: 1,
            departmentBranchId: null,
        });
    }, [navigate, store.departmentBranches, store.isDepartmentBranchesLoaded, urlParams]);

    useEffect(() => {
        if (!store.isVacancyRegionsLoaded || urlParams.vacancyRegionIds.length === 0) {
            return;
        }

        const validRegionIds = urlParams.vacancyRegionIds.filter(regionId =>
            Store.vacancyRegions.some(item => item.id === regionId)
        );

        if (validRegionIds.length === urlParams.vacancyRegionIds.length) {
            return;
        }

        navigate(ROUTES.VACANCIES(), {
            ...urlParams,
            page: 1,
            vacancyRegionIds: validRegionIds,
        });
    }, [navigate, store.isVacancyRegionsLoaded, urlParams, Store.vacancyRegions]);

    return (
        <LayoutBody>
            <LayoutContent>
                <LayoutContentTitle title={'Вакансии'} className='vacancies-page__title'>
                    <UiFilter
                        groups={[
                            {
                                name: 'departmentBranchId',
                                title: 'Отдел',
                                mode: 'single',
                                value: urlParams.departmentBranchId,
                                items: store.departmentBranches,
                                onChange: handleChangeDepartmentBranchId,
                            }, {
                                name: 'vacancyRegionIds',
                                title: 'Регион',
                                mode: 'multiple',
                                value: urlParams.vacancyRegionIds,
                                items: Store.vacancyRegions,
                                onChange: handleChangeVacancyRegionIds,
                            }]
                        }
                        onResetAll={handleResetFilters}
                    />
                    <UiButton style={{marginLeft: 16}} label={'Сбросить'} isLink onClick={handleClear}/>
                </LayoutContentTitle>
                <UiDataBoundary
                    className='vacancies-page__list'
                    isLoading={Store.isLoading && !Store.isInfiniteLoading}
                    isError={!Store.isSuccess}
                    onAction={() => Service.fetchItems(urlParams)}
                >
                    <UiList items={Store.items} component={VacancyItem}/>
                </UiDataBoundary>
                <UiPagination
                    page={urlParams.page}
                    pages={Store.pages}
                    onChange={handleChangePage}
                />
            </LayoutContent>
            <LayoutAside widgets={useUserWidgets()}/>
        </LayoutBody>
    );
});
